<script setup>
import { computed, defineProps } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

const { t } = useI18n()
const store = useStore()

const props = defineProps({
  selectItem: {
    type: Object,
    default: () => ({}), // 兜底空对象，避免遍历 selectItem.label 报错
  },
  routeName: {
    type: String,
    default: '',
  },
  curData: {
    type: Object,
    default: () => ({}),
  },
})

const optionsMap = computed(() => {
  const getters = store.getters
  return {
    faceEmotion: getters.faceEmotionOptions,
    faceAge: getters.faceAgeOptions,
    bodyAttribute: getters.bodyAttributeOptions,
    bodyDriver: getters.bodyDriverOptions,
    faceMask: getters.faceMaskOptions,
    bodyhelmet: getters.bodyhelmetOptions,
    detectionFire: getters.detectionFireOptions,
    bodyHeadShoulder: getters.bodyHeadShoulderOptions,
    bodyGesture: getters.bodyGestureOptions,
    bodyDetectionSmoking: getters.bodyDetectionSmokingOptions,
    bodyReflectiveVestDetection: getters.bodyReflectiveVestDetectionOptions,
    classificationAnimal: getters.classificationAnimalOptions,
    classificationDishes: getters.classificationDishesOptions,
    classificationGeneral: getters.classificationGeneralOptions,
    classificationVegetables: getters.classificationVegetablesOptions,
    classificationVehicleType: getters.classificationVehicleTypeOptions,
  }
})

const currentRouteOptions = computed(() => {
  const { routeName } = props
  return optionsMap.value[routeName] || {}
})

// 5. 预定义路由分组（消除模板中重复的数组字面量，提升可维护性）
const routeGroups = computed(() => ({
  // 展示进度条的路由分组
  progressRoutes: [
    'faceEmotion',
    'classificationGeneral',
    'faceAppearanceLevel',
    'classificationAnimal',
    'classificationVegetables',
    'classificationVehicleType',
    'classificationDishes',
    'classificationLandmark',
    'ocrWaterMeter',
  ],
  // OCR 相关路由分组
  ocrRoutes: [
    'ocrBusinessLicense',
    'ocrBankCard',
    'ocrInvoiceInformation',
    'ocrDriverLicense',
    'ocrHandwritten',
  ],
  // 不展示内容的路由分组
  hiddenRoutes: [
    'bodyHumanSegmentation',
    'bodyDetection',
    'vehicleDetection',
    'imageTechSuperResolution',
    'bodyHandKeypoint',
    'FaceDetection',
    'Landmarks',
    'imageTechDefogging',
    'imageTechColorEnhancement',
  ],
}))

const isProgressRoute = computed(() =>
  routeGroups.value.progressRoutes.includes(props.routeName),
)
const isOcrRoute = computed(() =>
  routeGroups.value.ocrRoutes.includes(props.routeName),
)
const isHiddenRoute = computed(() =>
  routeGroups.value.hiddenRoutes.includes(props.routeName),
)
</script>

<template>
  <div>
    <div v-if="isProgressRoute" class="emotion-progress">
      <div v-for="item in selectItem.label" class="flex mb10">
        <a
          v-if="
            ['faceAppearanceLevel', 'classificationLandmark'].includes(
              routeName,
            )
          "
          >{{ item.label }}:
        </a>
        <a
          class="animal-min"
          v-else-if="['classificationAnimal'].includes(routeName)"
          >{{ optionsMap.classificationAnimal[item.label] || item.label }} :
        </a>
        <a
          class="dish-min"
          v-else-if="['classificationDishes'].includes(routeName)"
          >{{ optionsMap.classificationDishes[item.label] || item.label }} :
        </a>
        <a v-else-if="['classificationGeneral'].includes(routeName)"
          >{{ optionsMap.classificationGeneral[item.label] || item.label }} :
        </a>
        <a v-else-if="['classificationVegetables'].includes(routeName)"
          >{{ optionsMap.classificationVegetables[item.label] || item.label }} :
        </a>
        <a v-else-if="['classificationVehicleType'].includes(routeName)"
          >{{ optionsMap.classificationVehicleType[item.label] || item.label }}
          :
        </a>
        <a v-else>{{ optionsMap.faceEmotion[item.label] || item.label }} :</a>
        <a-progress
          strokeColor="#ff8100"
          class="progress"
          :percent="+(item.value * 100).toFixed(2)"
        />
      </div>
    </div>

    <div v-else-if="routeName === 'faceAge'">
      <div v-for="(item, index) in selectItem.label" class="flex mb10">
        <span class="mr20 w-72">{{ item.label }} :</span>
        <a-progress
          style="width: 350px"
          strokeColor="#ff8100"
          class="progress"
          :percent="+(item.value * 100).toFixed(2)"
        />
      </div>
    </div>

    <div v-else-if="routeName === 'faceGender'">
      <div v-for="(item, index) in selectItem.label" class="flex mb10">
        <span class="mr20"
          >{{ optionsMap.faceAge[item.label] || item.label }}:</span
        >
        <a-progress
          style="width: 350px"
          strokeColor="#ff8100"
          class="progress"
          :percent="+(item.value * 100).toFixed(2)"
        />
      </div>
    </div>

    <div v-else-if="routeName === 'bodyAttribute'">
      <div v-for="(item, index) in selectItem.label" class="mb10">
        <span class="attribute"
          >{{ optionsMap.bodyAttribute[item.label] || item.label }}:</span
        >
        <a
          v-if="
            item.value &&
            typeof item.value === 'string' &&
            item.value.split(' ').length > 1
          "
        >
          <a v-for="i in item.value.split(' ')" class="ml0">
            {{ optionsMap.bodyAttribute[i] }}
          </a>
        </a>
        <a v-else>{{ optionsMap.bodyAttribute[item.value] }}</a>
      </div>
    </div>

    <div v-else-if="isOcrRoute">
      <div v-for="(item, index) in selectItem.label" class="flex mb10">
        <span
          :class="[
            { min: routeName === 'ocrInvoiceInformation' },
            'mr20',
            'ocr-min',
          ]"
          v-if="item.label"
          >{{ item.label }}:</span
        >
        <span class="wh100">{{ item.value }}</span>
      </div>
    </div>

    <div v-else-if="routeName === 'bodyKeypoint'" style="text-align: center">
      <img :src="selectItem.url" height="200px" style="margin-bottom: 10px" />
    </div>
    <div v-else-if="routeName === 'qrCode'">
      结果：<a style="marginleft: 0">{{ selectItem.label }}</a>
    </div>
    <div v-else-if="routeName === 'bodyHelmetDetection'">
      结果：<a>{{
        optionsMap.bodyhelmet[selectItem.label] || selectItem.label
      }}</a>
    </div>
    <div v-else-if="routeName === 'faceMaskDetection'">
      结果：<a>{{
        optionsMap.faceMask[selectItem.label] || selectItem.label
      }}</a>
    </div>
    <div v-else-if="routeName === 'bodyDriverMonitoring'">
      结果：<a>{{
        optionsMap.bodyDriver[selectItem.label] || selectItem.label
      }}</a>
    </div>
    <div v-else-if="routeName === 'detectionFire'">
      结果：<a>{{
        optionsMap.detectionFire[selectItem.label] || selectItem.label
      }}</a>
    </div>
    <div v-else-if="routeName === 'bodyHeadShoulderDetection'">
      结果：<a>{{
        optionsMap.bodyHeadShoulder[selectItem.label] || selectItem.label
      }}</a>
    </div>
    <div v-else-if="routeName === 'bodyCrowdDensity'">
      结果：<a>{{ selectItem.label }} </a>
      {{ t('home.person') }}
    </div>
    <div v-else-if="routeName === 'bodyGestureRecognition'">
      结果：<a>{{
        optionsMap.bodyGesture[selectItem.label] || selectItem.label
      }}</a>
    </div>
    <div v-else-if="routeName === 'bodyDetectionSmoking'">
      结果：<a>{{
        optionsMap.bodyDetectionSmoking[selectItem.label] || selectItem.label
      }}</a>
    </div>
    <div v-else-if="routeName === 'bodyReflectiveVestDetection'">
      结果：<a>{{
        optionsMap.bodyReflectiveVestDetection[selectItem.label] ||
        selectItem.label
      }}</a>
    </div>

    <div v-else-if="isHiddenRoute"></div>

    <div v-else>
      <span>结果：</span><a>{{ selectItem.label }}</a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.flex {
  display: flex;
}
.mb10 {
  margin-bottom: 0px;
  height: 27px;
}
.ml0 {
  margin-left: 0 !important;
  margin-right: 5px;
}
.wh100 {
  margin-left: 20px;
}
.animal-min {
  min-width: 135px;
}
.ocr-min {
  min-width: 120px;
}
.min {
  min-width: 145px !important;
}
.dish-min {
  min-width: 105px;
}

.attribute {
  min-width: 140px;
  display: inline-block;
}
.emotion-progress {
  a {
    margin-left: 0 !important;
    margin-right: 40px;
    max-width: 300px;
    color: #000 !important;
  }

  .progress {
    margin-right: 40px;
    flex: 1;
  }
}

.right-dec a {
  margin-left: 10px;
  color: #ff0000;
  word-wrap: break-word;
}

.w-72 {
  width: 72px;
  text-align: right;
}
</style>
